import { useEffect, useState } from "react";
import {
  Bell,
  Plus,
  X,
  PaperPlaneTilt,
  Users,
  UsersThree,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import api, { formatDate } from "../lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";

export default function Notificaciones() {
  const { can } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [recipientsModal, setRecipientsModal] = useState(null);

  const [events, setEvents] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [recipientsOpen, setRecipientsOpen] = useState(false);

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");

  const [form, setForm] = useState({
    user_ids: [],
    group_ids: [],
    title: "",
    body: "",
    type: "info",
    entity_id: "",
  });

  // =========================================================
  // NOTIFICACIONES
  // =========================================================

  const loadNotifications = async () => {
    try {
      setLoading(true);

      const { data } = await api.get("/notificaciones");

      setNotifications(data || []);
    } catch (error) {
      console.error(error);

      toast.error("No se pudieron cargar las notificaciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  // =========================================================
  // USUARIOS Y GRUPOS
  // =========================================================

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);

      const { data } = await api.get("/users/tenant_users");

      setUsers(data || []);

      return data || [];
    } catch (error) {
      console.error(error);

      toast.error("No se pudieron cargar los usuarios");

      return [];
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadGroups = async () => {
    try {
      setLoadingGroups(true);

      const { data } = await api.get("/grupos");

      setGroups(data || []);

      return data || [];
    } catch (error) {
      console.error(error);

      toast.error("No se pudieron cargar los grupos");

      return [];
    } finally {
      setLoadingGroups(false);
    }
  };

  // =========================================================
  // EVENTOS Y DOCUMENTOS
  // =========================================================

  useEffect(() => {
    const loadEntities = async () => {
      if (form.type === "info") {
        return;
      }

      try {
        setLoadingEntities(true);

        if (form.type === "event") {
          const { data } = await api.get("/eventos/futuros");

          setEvents(data || []);
        }

        if (form.type === "document") {
          const { data } = await api.get("/documentos/shared/public");

          setDocuments(data || []);
        }
      } catch (error) {
        console.error(error);

        toast.error(
          form.type === "event"
            ? "No se pudieron cargar los eventos"
            : "No se pudieron cargar los documentos",
        );
      } finally {
        setLoadingEntities(false);
      }
    };

    loadEntities();
  }, [form.type]);

  // =========================================================
  // MODAL
  // =========================================================

  const openModal = async () => {
    setUserSearch("");
    setGroupSearch("");

    setForm({
      user_ids: [],
      group_ids: [],
      title: "",
      body: "",
      type: "info",
      entity_id: "",
    });

    setModalOpen(true);

    await Promise.all([loadUsers(), loadGroups()]);
  };

  const closeModal = () => {
    if (creating) return;

    setModalOpen(false);
  };

  // =========================================================
  // FORMULARIO
  // =========================================================

  const handleTypeChange = (event) => {
    const type = event.target.value;

    setForm((current) => ({
      ...current,
      type,
      entity_id: "",
    }));
  };

  const selectedUserIds = Array.isArray(form.user_ids) ? form.user_ids : [];

  const selectedGroupIds = Array.isArray(form.group_ids) ? form.group_ids : [];

  const allUsersSelected =
    users.length > 0 &&
    selectedUserIds.length === users.length &&
    users.every((user) => selectedUserIds.includes(user.id));

  const toggleGroup = (groupId) => {
    setForm((current) => {
      const currentIds = Array.isArray(current.group_ids)
        ? current.group_ids
        : [];

      const isSelected = currentIds.includes(groupId);

      return {
        ...current,
        group_ids: isSelected
          ? currentIds.filter((id) => id !== groupId)
          : [...currentIds, groupId],
      };
    });
  };

  const toggleUser = (userId) => {
    setForm((current) => {
      const currentIds = Array.isArray(current.user_ids)
        ? current.user_ids
        : [];

      const isSelected = currentIds.includes(userId);

      return {
        ...current,
        user_ids: isSelected
          ? currentIds.filter((id) => id !== userId)
          : [...currentIds, userId],
      };
    });
  };

  const toggleAllUsers = (event) => {
    const checked = event.target.checked;

    setForm((current) => ({
      ...current,
      user_ids: checked ? users.map((user) => user.id) : [],
      group_ids: checked ? [] : current.group_ids,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }

    if (!form.body.trim()) {
      toast.error("El mensaje es obligatorio");
      return;
    }

    if (
      (form.type === "event" || form.type === "document") &&
      !form.entity_id
    ) {
      toast.error(
        form.type === "event"
          ? "Debes seleccionar un evento"
          : "Debes seleccionar un documento",
      );

      return;
    }

    if (
      selectedUserIds.length === 0 &&
      selectedGroupIds.length === 0 &&
      !allUsersSelected
    ) {
      toast.error("Debes seleccionar al menos un destinatario");
      return;
    }

    try {
      setCreating(true);

      const sendingToAll = allUsersSelected && selectedGroupIds.length === 0;

      await api.post("/notificaciones", {
        user_ids: sendingToAll ? null : selectedUserIds,
        group_ids: selectedGroupIds.length > 0 ? selectedGroupIds : null,
        excluded_user_ids: null,
        title: form.title.trim(),
        body: form.body.trim(),
        type: form.type,
        entity_id:
          form.type === "event" || form.type === "document"
            ? form.entity_id
            : null,
      });

      toast.success("Notificación enviada correctamente");

      setModalOpen(false);

      loadNotifications();
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.detail || "No se pudo enviar la notificación",
      );
    } finally {
      setCreating(false);
    }
  };

  // =========================================================
  // BÚSQUEDA DE USUARIOS Y GRUPOS
  // =========================================================

  const filteredUsers = users.filter((user) => {
    const search = userSearch.trim().toLowerCase();

    if (!search) {
      return true;
    }

    const fullName = `${user.name || ""} ${
      user.family_name || ""
    }`.toLowerCase();

    const email = (user.email || "").toLowerCase();

    return fullName.includes(search) || email.includes(search);
  });

  const filteredGroups = groups.filter((group) => {
    const search = groupSearch.trim().toLowerCase();

    if (!search) {
      return true;
    }

    return (
      group.name?.toLowerCase().includes(search) ||
      group.description?.toLowerCase().includes(search)
    );
  });

  // =========================================================
  // FECHA
  // =========================================================

  const formatDateTime = (value) => {
    if (!value) return "";

    return new Date(value).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="p-6 md:p-10" data-testid="recibos-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
            Eventos
          </div>

          <h1 className="font-display text-4xl font-bold text-med-text">
            Notificaciones
          </h1>

          <p className="text-med-muted mt-1">Historial de notificaciones.</p>
        </div>

        {can(PERMISSIONS.MANAGE_NOTIFICATIONS) && (
          <button
            type="button"
            onClick={openModal}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-med-primary text-white font-medium hover:opacity-90 transition-opacity"
          >
            <Plus size={20} weight="bold" />
            Nueva notificación
          </button>
        )}
      </header>

      <div className="bg-med-surface border border-med-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-med-muted">
            Cargando notificaciones…
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-med-surface2 mx-auto grid place-items-center mb-4">
              <Bell size={28} className="text-med-muted" weight="duotone" />
            </div>

            <h2 className="font-semibold text-med-text">
              No hay notificaciones
            </h2>

            <p className="text-sm text-med-muted mt-1">
              Aún no se ha enviado ninguna notificación.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-med-border">
            {notifications.map((notification) => {
              const sentToAll = notification.sent_to_all;
              const recipientCount = notification.user_ids?.length || 0;

              return (
                <div key={notification.id} className="px-6 py-5">
                  <div className="flex items-start justify-between gap-6">
                    <div className="min-w-0 flex-1">
                      <h2 className="font-semibold text-med-text">
                        {notification.title}
                      </h2>

                      <p className="text-sm text-med-muted mt-1 whitespace-pre-wrap">
                        {notification.body}
                      </p>

                      {can(PERMISSIONS.MANAGE_NOTIFICATIONS) && (
                        <button
                          type="button"
                          onClick={() => setRecipientsModal(notification)}
                          className="inline-flex items-center gap-2 mt-3 text-sm text-med-primary hover:opacity-80 transition-opacity"
                        >
                          <Users size={18} />

                          {sentToAll
                            ? "Todos los usuarios"
                            : `${recipientCount} ${
                                recipientCount === 1
                                  ? "destinatario"
                                  : "destinatarios"
                              }`}
                        </button>
                      )}
                    </div>

                    <div className="text-xs text-med-muted whitespace-nowrap">
                      {formatDateTime(notification.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />

          <div className="relative w-full max-w-lg max-h-[calc(100vh-2rem)] bg-med-surface rounded-2xl border border-med-border shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-med-border">
              <h2 className="text-lg font-semibold text-med-text">
                Nueva notificación
              </h2>

              <button
                type="button"
                onClick={closeModal}
                className="p-2 rounded-lg hover:bg-med-surface2"
              >
                <X size={20} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="min-h-0 flex-1 overflow-y-auto p-6 space-y-5"
            >
              {/* DESTINATARIOS */}

              <div className="border border-med-border rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setRecipientsOpen((current) => !current)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-med-surface2 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-med-text">
                      Destinatarios
                    </span>

                    <span className="text-xs text-med-muted">
                      {allUsersSelected
                        ? "Todos"
                        : `${selectedUserIds.length} usuarios${
                            selectedGroupIds.length > 0
                              ? ` · ${selectedGroupIds.length} ${
                                  selectedGroupIds.length === 1
                                    ? "grupo"
                                    : "grupos"
                                }`
                              : ""
                          }`}
                    </span>
                  </div>

                  <span
                    className={`text-med-muted transition-transform ${
                      recipientsOpen ? "rotate-180" : ""
                    }`}
                  >
                    ▾
                  </span>
                </button>

                {recipientsOpen && (
                  <div className="border-t border-med-border">
                    {/* GRUPOS */}

                    <div className="border-b border-med-border">
                      <div className="flex items-center gap-2 px-4 py-3">
                        <UsersThree
                          size={18}
                          weight="duotone"
                          className="text-med-primary"
                        />

                        <span className="text-sm font-semibold text-med-text">
                          Grupos
                        </span>

                        {selectedGroupIds.length > 0 && (
                          <span className="text-xs text-med-muted">
                            {selectedGroupIds.length} seleccionados
                          </span>
                        )}
                      </div>

                      <div className="px-3 pb-3">
                        <input
                          type="text"
                          value={groupSearch}
                          onChange={(event) =>
                            setGroupSearch(event.target.value)
                          }
                          placeholder="Buscar grupo..."
                          className="w-full px-3 py-2 rounded-lg bg-med-bg border border-med-border outline-none focus:ring-2 focus:ring-med-primary/30"
                        />
                      </div>

                      <div className="max-h-48 overflow-y-auto pb-2">
                        {loadingGroups ? (
                          <div className="px-4 py-6 text-sm text-center text-med-muted">
                            Cargando grupos...
                          </div>
                        ) : filteredGroups.length === 0 ? (
                          <div className="px-4 py-6 text-sm text-center text-med-muted">
                            No se encontraron grupos.
                          </div>
                        ) : (
                          filteredGroups.map((group) => {
                            const checked = selectedGroupIds.includes(group.id);

                            return (
                              <div
                                key={group.id}
                                className="flex items-center gap-3 px-4 py-2 hover:bg-med-surface2 transition-colors"
                              >
                                <input
                                  id={`group-${group.id}`}
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleGroup(group.id)}
                                  className="w-4 h-4 accent-med-primary cursor-pointer"
                                />

                                <label
                                  htmlFor={`group-${group.id}`}
                                  className="min-w-0 flex-1 cursor-pointer"
                                >
                                  <div className="text-sm font-medium text-med-text">
                                    {group.name}
                                  </div>

                                  <div className="text-xs text-med-muted">
                                    {group.user_count || 0}{" "}
                                    {group.user_count === 1
                                      ? "usuario"
                                      : "usuarios"}
                                  </div>

                                  {group.description && (
                                    <div className="text-xs text-med-muted truncate">
                                      {group.description}
                                    </div>
                                  )}
                                </label>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* USUARIOS */}

                    <div>
                      <div className="flex items-center gap-2 px-4 py-3">
                        <Users
                          size={18}
                          weight="duotone"
                          className="text-med-primary"
                        />

                        <span className="text-sm font-semibold text-med-text">
                          Usuarios
                        </span>

                        {selectedUserIds.length > 0 && (
                          <span className="text-xs text-med-muted">
                            {selectedUserIds.length} seleccionados
                          </span>
                        )}
                      </div>

                      {/* BUSCADOR */}

                      <div className="px-3 pb-3">
                        <input
                          type="text"
                          value={userSearch}
                          onChange={(event) =>
                            setUserSearch(event.target.value)
                          }
                          placeholder="Buscar por nombre o email..."
                          className="w-full px-3 py-2 rounded-lg bg-med-bg border border-med-border outline-none focus:ring-2 focus:ring-med-primary/30"
                        />
                      </div>

                      {/* SELECCIONAR TODOS */}

                      <div className="flex items-center gap-3 px-4 py-3 border-y border-med-border">
                        <input
                          id="select-all-users"
                          type="checkbox"
                          checked={allUsersSelected}
                          onChange={toggleAllUsers}
                          className="w-4 h-4 accent-med-primary cursor-pointer"
                        />

                        <label
                          htmlFor="select-all-users"
                          className="font-medium text-sm text-med-text cursor-pointer select-none"
                        >
                          Todos los usuarios
                        </label>
                      </div>

                      {/* LISTA DE USUARIOS */}

                      <div className="h-64 overflow-y-auto">
                        {loadingUsers ? (
                          <div className="px-4 py-6 text-sm text-center text-med-muted">
                            Cargando usuarios...
                          </div>
                        ) : filteredUsers.length === 0 ? (
                          <div className="px-4 py-6 text-sm text-center text-med-muted">
                            No se encontraron usuarios.
                          </div>
                        ) : (
                          filteredUsers.map((user) => {
                            const name = [user.name, user.family_name]
                              .filter(Boolean)
                              .join(" ");

                            const checked = selectedUserIds.includes(user.id);

                            return (
                              <div
                                key={user.id}
                                className="flex items-center gap-3 px-4 py-2 hover:bg-med-surface2 transition-colors"
                              >
                                <input
                                  id={`user-${user.id}`}
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleUser(user.id)}
                                  className="w-4 h-4 accent-med-primary cursor-pointer"
                                />

                                <label
                                  htmlFor={`user-${user.id}`}
                                  className="min-w-0 flex-1 cursor-pointer"
                                >
                                  <div className="text-sm font-medium text-med-text">
                                    {name || "Usuario"}
                                  </div>

                                  {user.email && (
                                    <div className="text-xs text-med-muted truncate">
                                      {user.email}
                                    </div>
                                  )}
                                </label>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* TIPO */}

              <div>
                <label className="block text-sm font-medium text-med-text mb-2">
                  Tipo
                </label>

                <select
                  value={form.type}
                  onChange={handleTypeChange}
                  className="w-full px-3 py-2.5 rounded-xl border border-med-border bg-med-bg outline-none focus:ring-2 focus:ring-med-primary/30"
                >
                  <option value="info">Información</option>
                  <option value="event">Evento</option>
                  <option value="document">Documento</option>
                </select>
              </div>

              {/* EVENTO */}

              {form.type === "event" && (
                <div>
                  <label className="block text-sm font-medium text-med-text mb-2">
                    Evento
                  </label>

                  <select
                    value={form.entity_id}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        entity_id: event.target.value,
                      }))
                    }
                    disabled={loadingEntities}
                    className="w-full px-3 py-2.5 rounded-xl border border-med-border bg-med-bg outline-none focus:ring-2 focus:ring-med-primary/30 disabled:opacity-50"
                  >
                    <option value="">
                      {loadingEntities
                        ? "Cargando eventos..."
                        : "Selecciona un evento"}
                    </option>

                    {events.map((evento) => (
                      <option key={evento.id} value={evento.id}>
                        {evento.titulo} ({formatDate(evento.fecha_inicio)})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* DOCUMENTO */}

              {form.type === "document" && (
                <div>
                  <label className="block text-sm font-medium text-med-text mb-2">
                    Documento
                  </label>

                  <select
                    value={form.entity_id}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        entity_id: event.target.value,
                      }))
                    }
                    disabled={loadingEntities}
                    className="w-full px-3 py-2.5 rounded-xl border border-med-border bg-med-bg outline-none focus:ring-2 focus:ring-med-primary/30 disabled:opacity-50"
                  >
                    <option value="">
                      {loadingEntities
                        ? "Cargando documentos..."
                        : "Selecciona un documento"}
                    </option>

                    {documents.map((documento) => (
                      <option key={documento.id} value={documento.id}>
                        {documento.titulo}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* TÍTULO */}

              <div>
                <label className="block text-sm font-medium text-med-text mb-2">
                  Título
                </label>

                <input
                  type="text"
                  value={form.title}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2.5 rounded-xl border border-med-border bg-med-bg outline-none focus:ring-2 focus:ring-med-primary/30"
                  placeholder="Título de la notificación"
                  autoFocus
                />
              </div>

              {/* MENSAJE */}

              <div>
                <label className="block text-sm font-medium text-med-text mb-2">
                  Mensaje
                </label>

                <textarea
                  value={form.body}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      body: event.target.value,
                    }))
                  }
                  rows={5}
                  className="w-full px-3 py-2.5 rounded-xl border border-med-border bg-med-bg outline-none focus:ring-2 focus:ring-med-primary/30 resize-none"
                  placeholder="Escribe el mensaje..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 rounded-xl border border-med-border text-med-text hover:bg-med-surface2"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={creating}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-med-primary text-white font-medium disabled:opacity-50"
                >
                  <PaperPlaneTilt size={18} weight="fill" />

                  {creating ? "Enviando…" : "Enviar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DESTINATARIOS */}

      {recipientsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setRecipientsModal(null)}
          />

          <div className="relative flex w-full max-w-md max-h-[calc(100vh-2rem)] flex-col bg-med-surface rounded-2xl border border-med-border shadow-xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-med-border shrink-0">
              <div>
                <h2 className="text-lg font-semibold text-med-text">
                  Destinatarios
                </h2>

                <p className="text-sm text-med-muted mt-1">
                  {recipientsModal.sent_to_all
                    ? "Todos los usuarios"
                    : `${recipientsModal.user_ids?.length || 0} usuarios`}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setRecipientsModal(null)}
                className="p-2 rounded-lg hover:bg-med-surface2 shrink-0"
              >
                <X size={20} />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {recipientsModal.recipients?.length > 0 ? (
                <div className="p-3 space-y-1">
                  {recipientsModal.recipients.map((recipient) => {
                    return (
                      <div
                        key={recipient.id}
                        className="px-4 py-2 rounded-xl hover:bg-med-surface2 transition-colors"
                      >
                        <div className="text-sm font-medium text-med-text">
                          {recipient.email}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center text-med-muted">
                  No se encontraron destinatarios.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
